// Live solve-state engine. Pure data — no canvas, no localStorage, no DOM.
// Mutating ops push a `SolveEvent` onto `history` and clear `redoStack`.
// Givens are immutable. errorsMade/hintsUsed are session-cumulative — undo
// does NOT decrement them (the user really did make that error / use that hint).

import {
  cloneGrid,
  CELL_COUNT,
  peerIndices,
  type Digit,
  type NonEmptyDigit,
  type Puzzle,
} from './types.js';

export type SolveEvent =
  | {
      readonly kind: 'enter';
      readonly cellIndex: number;
      readonly prevDigit: Digit;
      readonly newDigit: NonEmptyDigit;
      readonly clearedPencils: ReadonlySet<NonEmptyDigit>;
      /** Peer cells (same row/col/box) whose pencil set contained `newDigit`
       *  before this entry, so undo can put each mark back. */
      readonly clearedPeerPencils: ReadonlyArray<number>;
      readonly wasError: boolean;
    }
  | {
      readonly kind: 'erase';
      readonly cellIndex: number;
      readonly prevDigit: Digit;
      readonly prevPencils: ReadonlySet<NonEmptyDigit>;
    }
  | {
      readonly kind: 'pencil';
      readonly cellIndex: number;
      readonly digit: NonEmptyDigit;
      readonly added: boolean;
    };

export interface GameStateData {
  readonly puzzle: Puzzle;
  readonly user: Digit[];
  readonly pencil: Array<Set<NonEmptyDigit>>;
  readonly history: SolveEvent[];
  readonly redoStack: SolveEvent[];
  readonly startedAt: number;
  hintsUsed: number;
  errorsMade: number;
}

export function createState(puzzle: Puzzle, now: number = Date.now()): GameStateData {
  const pencil: Array<Set<NonEmptyDigit>> = new Array(CELL_COUNT);
  for (let i = 0; i < CELL_COUNT; i++) {
    pencil[i] = new Set<NonEmptyDigit>();
  }
  return {
    puzzle,
    user: cloneGrid(puzzle.givens),
    pencil,
    history: [],
    redoStack: [],
    startedAt: now,
    hintsUsed: 0,
    errorsMade: 0,
  };
}

function isGiven(state: GameStateData, cellIndex: number): boolean {
  return state.puzzle.givens[cellIndex] !== 0;
}

function getPencil(state: GameStateData, cellIndex: number): Set<NonEmptyDigit> {
  const set = state.pencil[cellIndex];
  if (set === undefined) {
    throw new Error(`pencil[${cellIndex}] missing — state corrupted`);
  }
  return set;
}

export function enterDigit(
  state: GameStateData,
  cellIndex: number,
  digit: NonEmptyDigit,
): boolean {
  if (isGiven(state, cellIndex)) return false;

  const prevDigit = state.user[cellIndex] as Digit;
  const pencilSet = getPencil(state, cellIndex);
  const clearedPencils: ReadonlySet<NonEmptyDigit> = new Set(pencilSet);

  state.user[cellIndex] = digit;
  pencilSet.clear();

  // Eager-elimination: drop this digit from every row/col/box peer's pencil
  // set. Matches what a careful solver does by hand and keeps the pencil
  // grid honest as constraints accumulate.
  const clearedPeerPencils: number[] = [];
  for (const peer of peerIndices(cellIndex)) {
    const peerSet = getPencil(state, peer);
    if (peerSet.delete(digit)) clearedPeerPencils.push(peer);
  }

  const wasError = digit !== state.puzzle.solution[cellIndex];
  if (wasError && prevDigit !== digit) {
    state.errorsMade += 1;
  }

  state.history.push({
    kind: 'enter',
    cellIndex,
    prevDigit,
    newDigit: digit,
    clearedPencils,
    clearedPeerPencils,
    wasError,
  });
  state.redoStack.length = 0;
  return true;
}

export function togglePencil(
  state: GameStateData,
  cellIndex: number,
  digit: NonEmptyDigit,
): boolean {
  if (isGiven(state, cellIndex)) return false;
  if (state.user[cellIndex] !== 0) return false;

  const pencilSet = getPencil(state, cellIndex);
  const added = !pencilSet.has(digit);
  if (added) pencilSet.add(digit);
  else pencilSet.delete(digit);

  state.history.push({ kind: 'pencil', cellIndex, digit, added });
  state.redoStack.length = 0;
  return true;
}

export function erase(state: GameStateData, cellIndex: number): boolean {
  if (isGiven(state, cellIndex)) return false;
  const pencilSet = getPencil(state, cellIndex);
  const prevDigit = state.user[cellIndex] as Digit;
  if (prevDigit === 0 && pencilSet.size === 0) return false;

  const prevPencils: ReadonlySet<NonEmptyDigit> = new Set(pencilSet);
  state.user[cellIndex] = 0;
  pencilSet.clear();

  state.history.push({ kind: 'erase', cellIndex, prevDigit, prevPencils });
  state.redoStack.length = 0;
  return true;
}

function applyReverse(state: GameStateData, event: SolveEvent): void {
  if (event.kind === 'enter') {
    state.user[event.cellIndex] = event.prevDigit;
    const target = getPencil(state, event.cellIndex);
    target.clear();
    for (const d of event.clearedPencils) target.add(d);
    for (const peer of event.clearedPeerPencils) {
      getPencil(state, peer).add(event.newDigit);
    }
    return;
  }
  if (event.kind === 'erase') {
    state.user[event.cellIndex] = event.prevDigit;
    const target = getPencil(state, event.cellIndex);
    target.clear();
    for (const d of event.prevPencils) target.add(d);
    return;
  }
  const target = getPencil(state, event.cellIndex);
  if (event.added) target.delete(event.digit);
  else target.add(event.digit);
}

function applyForward(state: GameStateData, event: SolveEvent): void {
  if (event.kind === 'enter') {
    state.user[event.cellIndex] = event.newDigit;
    getPencil(state, event.cellIndex).clear();
    for (const peer of event.clearedPeerPencils) {
      getPencil(state, peer).delete(event.newDigit);
    }
    return;
  }
  if (event.kind === 'erase') {
    state.user[event.cellIndex] = 0;
    getPencil(state, event.cellIndex).clear();
    return;
  }
  const target = getPencil(state, event.cellIndex);
  if (event.added) target.add(event.digit);
  else target.delete(event.digit);
}

export function undo(state: GameStateData): boolean {
  const event = state.history.pop();
  if (event === undefined) return false;
  applyReverse(state, event);
  state.redoStack.push(event);
  return true;
}

export function redo(state: GameStateData): boolean {
  const event = state.redoStack.pop();
  if (event === undefined) return false;
  applyForward(state, event);
  state.history.push(event);
  return true;
}

export function recordHint(state: GameStateData): void {
  state.hintsUsed += 1;
}
